import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, Copy, Check, Facebook, Linkedin, Twitter, ExternalLink,
  Sparkles, RefreshCw,
} from "lucide-react";
import { api } from "../../lib/api";

const SITE_BASE = "https://techemergency.com.au";

/**
 * Build the bot-friendly share URL. Facebook, LinkedIn and other social
 * scrapers don't run JavaScript, so we route them through the server-side
 * OG endpoint which renders correct per-post meta tags.
 */
function shareUrl(slug) {
  return `${SITE_BASE}/api/share/blog/${slug}`;
}

/** Pick 2–3 most relevant hashtags from a post's tags. */
function hashtags(tags = []) {
  return tags
    .slice(0, 3)
    .map((t) => "#" + t.replace(/[^a-z0-9]/gi, ""))
    .filter((t) => t.length > 1)
    .join(" ");
}

/** Default hook variants per platform. Authors can edit before copying. */
function defaultHook(post, platform) {
  const tags = hashtags(post.tags);
  const url = shareUrl(post.slug);
  const t = (post.title || "").replace(/—/g, "-");

  switch (platform) {
    case "facebook":
      return `${t}\n\nThe honest, plain-English version from Adelaide's mobile IT team.\n\n${url}\n\n${tags}`;
    case "linkedin":
      return `${t}\n\n${(post.excerpt || "").trim()}\n\nFull article ↓\n${url}\n\n${tags}`;
    case "twitter": {
      // Tight to fit Twitter's 280-char limit (URL counts as 23).
      const trimmedTitle = t.length > 140 ? t.slice(0, 137) + "…" : t;
      return `${trimmedTitle}\n\n${url}\n\n${tags}`.slice(0, 278);
    }
    default:
      return url;
  }
}

const PLATFORMS = [
  {
    id: "facebook",
    label: "Facebook",
    Icon: Facebook,
    color: "#1877F2",
    composeUrl: (url, text) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    Icon: Linkedin,
    color: "#0A66C2",
    composeUrl: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  {
    id: "twitter",
    label: "X / Twitter",
    Icon: Twitter,
    color: "#1DA1F2",
    composeUrl: (url, text) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
  },
];

export default function SharePanel() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="py-8 flex items-center gap-2 text-zinc-500 text-sm">
        <Loader2 size={14} className="animate-spin" /> Loading posts…
      </div>
    );
  }

  if (!posts.length) {
    return (
      <div className="te-card p-6 text-sm text-zinc-400">
        No published posts yet — go to the Blog Manager tab to add one.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="share-panel">
      {/* How-to header */}
      <div className="te-card p-5 border-white/10">
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 flex items-center justify-center flex-shrink-0"
            style={{ background: "var(--te-primary)" }}
          >
            <Sparkles size={16} color="#fff" />
          </div>
          <div className="text-sm text-zinc-300 leading-relaxed">
            <div className="text-base font-bold text-white mb-1">
              Share your posts — properly.
            </div>
            Click <strong>Open Facebook compose</strong> below and Facebook will
            preview the article with the correct title, excerpt and cover image
            (we route the share URL through a server-rendered Open Graph
            endpoint so it works even though your site is a React SPA). Edit the
            hook to sound like you, then post.
          </div>
        </div>
      </div>

      {posts.map((p) => (
        <PostShareCard key={p.id} post={p} />
      ))}
    </div>
  );
}

function PostShareCard({ post }) {
  const url = shareUrl(post.slug);
  const [platform, setPlatform] = useState("facebook");
  const platformDef = PLATFORMS.find((x) => x.id === platform);
  const [text, setText] = useState(() => defaultHook(post, platform));
  const [copied, setCopied] = useState(null);

  // Keep the hook in sync if the user switches platform.
  useEffect(() => {
    setText(defaultHook(post, platform));
  }, [platform, post.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async (key, val) => {
    try {
      await navigator.clipboard.writeText(val);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = val;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 1600);
  };

  const charCount = text.length;
  const tooLong = platform === "twitter" && charCount > 280;

  return (
    <div className="te-card p-5" data-testid={`share-card-${post.slug}`}>
      <div className="flex flex-col md:flex-row gap-5">
        {/* Post meta */}
        <div className="md:w-72 flex-shrink-0">
          {post.cover_image && (
            <div
              className="aspect-video mb-3 border border-white/10"
              style={{
                backgroundImage: `url(${post.cover_image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}
          <h3 className="text-sm font-bold tracking-tight leading-snug mb-2">
            {post.title}
          </h3>
          <p className="text-xs text-zinc-400 leading-relaxed mb-3 line-clamp-3">
            {post.excerpt}
          </p>
          <a
            href={`/blog/${post.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium inline-flex items-center gap-1.5 text-zinc-500 hover:text-white transition"
          >
            View live post <ExternalLink size={11} />
          </a>
        </div>

        {/* Compose */}
        <div className="flex-1 min-w-0">
          {/* Platform switcher */}
          <div className="flex gap-1 mb-3 border-b border-white/10">
            {PLATFORMS.map((pl) => (
              <button
                key={pl.id}
                type="button"
                onClick={() => setPlatform(pl.id)}
                data-testid={`share-platform-${pl.id}-${post.slug}`}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                  platform === pl.id ? "text-white" : "text-zinc-500 hover:text-zinc-200"
                }`}
                style={{
                  borderBottomColor:
                    platform === pl.id ? "var(--te-primary)" : "transparent",
                }}
              >
                <pl.Icon size={13} /> {pl.label}
              </button>
            ))}
          </div>

          {/* Share URL */}
          <div className="mb-3">
            <label className="te-overline text-[9px] block mb-1.5">
              / Bot-friendly share URL
            </label>
            <div className="flex gap-2">
              <input
                readOnly
                value={url}
                data-testid={`share-url-${post.slug}`}
                className="flex-1 min-w-0 bg-black/40 border border-white/10 px-3 py-2 text-xs font-mono text-zinc-200 focus:outline-none focus:border-[var(--te-primary)]"
              />
              <button
                type="button"
                onClick={() => copy("url", url)}
                data-testid={`share-copy-url-${post.slug}`}
                className="te-btn te-btn-ghost text-xs whitespace-nowrap"
              >
                {copied === "url" ? (
                  <>
                    <Check size={12} style={{ color: "var(--te-primary)" }} />{" "}
                    Copied
                  </>
                ) : (
                  <>
                    <Copy size={12} /> Copy
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Hook editor */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5">
              <label className="te-overline text-[9px]">
                / Your {platformDef.label} hook (edit before posting)
              </label>
              <span
                className={`text-[10px] font-mono ${
                  tooLong ? "text-red-400" : "text-zinc-500"
                }`}
              >
                {charCount}
                {platform === "twitter" && " / 280"}
              </span>
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              data-testid={`share-hook-${post.slug}`}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 leading-snug focus:outline-none focus:border-[var(--te-primary)] resize-y"
            />
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => copy("text", text)}
              data-testid={`share-copy-text-${post.slug}`}
              className="te-btn te-btn-ghost text-xs"
            >
              {copied === "text" ? (
                <>
                  <Check size={12} style={{ color: "var(--te-primary)" }} />{" "}
                  Copied
                </>
              ) : (
                <>
                  <Copy size={12} /> Copy hook
                </>
              )}
            </button>
            <a
              href={platformDef.composeUrl(url, text)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`share-open-${platform}-${post.slug}`}
              className="te-btn te-btn-primary text-xs"
              style={{ background: platformDef.color, color: "#fff" }}
            >
              <platformDef.Icon size={13} /> Open {platformDef.label} compose
              <ExternalLink size={11} />
            </a>
          </div>

          {platform === "facebook" && (
            <div className="mt-3 space-y-2">
              <div className="text-[11px] text-zinc-500 leading-relaxed">
                Facebook&apos;s share popup pre-fills the link &amp; image but the
                custom &ldquo;quote&rdquo; text only appears for Page admins.
                Easiest path: copy the hook → click <em>Open Facebook compose</em>{" "}
                → paste into the post composer that opens.
              </div>
              <a
                href={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(url)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`share-refresh-fb-${post.slug}`}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-zinc-400 hover:text-white transition border border-white/10 hover:border-white/30 px-2.5 py-1.5"
              >
                <RefreshCw size={11} /> Already shared? Refresh Facebook&apos;s cache
                <ExternalLink size={10} />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
